import { HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, Observable, of, throwError } from "rxjs";
import { ApplicationsResponse } from "./applications.model";
import { environment } from "../../../../environments/environment";
import { API_HEADERS } from "../../../shared/api-headers";
import { ApiClient } from "../../../shared/data/api.client";
import { userContextStore } from "../../../shared/data/user-context.store";
import { HANDLE_HTTP_ERROR_LOCALLY } from "../../../shared/error/http-error-context";

@Injectable({ providedIn: "root" })
export class ApplicationsClient {
  private readonly api = inject(ApiClient);
  private readonly userContext = inject(userContextStore);

  loadApplications(): Observable<ApplicationsResponse> {
    const userId = this.currentUserId();
    return this.api
      .get<ApplicationsResponse>(
        environment.api.services.commons.applicationsV1,
        {
          headers: {
            [API_HEADERS.acceptLanguage]: this.userContext.language(),
            [API_HEADERS.idmRoles]: "",
            ...(userId ? { [API_HEADERS.userId]: userId } : {}),
          },
          params: { country: this.userContext.country() },
        },
      )
      .pipe(
        catchError((error) => {
          console.error(
            "[ApplicationsClient] failed to load applications",
            error,
          );
          return of({ categories: [] });
        }),
      );
  }

  favoriteApplication(applicationId: string): Observable<void> {
    return this.updateFavorite(applicationId, true);
  }

  unfavoriteApplication(applicationId: string): Observable<void> {
    return this.updateFavorite(applicationId, false);
  }

  private currentUserId(): string | null {
    return this.userContext.userContext()?.id.trim() || null;
  }

  private updateFavorite(
    applicationId: string,
    favorite: boolean,
  ): Observable<void> {
    const userId = this.currentUserId();
    if (!userId) {
      return throwError(() => new Error("User identity is not available"));
    }

    const path = `${environment.api.services.commons.applicationsV1}/favorites/${encodeURIComponent(applicationId)}`;
    const options = {
      context: new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true),
      headers: {
        [API_HEADERS.idmRoles]: "",
        [API_HEADERS.userId]: userId,
      },
    };
    return favorite
      ? this.api.put<void>(path, null, options)
      : this.api.delete<void>(path, options);
  }
}
